/**
 * Der Vespera-Stern nimmt die Farbe der Stunde an. (15.09.2026, Ticket 2297)
 *
 * Martin wünscht sich „ein dynamisches Element … mit dem Mond und der Sonne"
 * als Erkennungswert, so wie der wandernde Grund auf vespera.at. Die Rechnung
 * dazu steht in tagesszene.ts ({@link sternstand}) — hier wird sie nur an das
 * Zeichen im Kopf gelegt.
 *
 * ⚠️ Kein neuer Rechenweg und keine neue Farbe: Mischverhältnis, Winkel und
 *    Hof entstehen ausschließlich aus `--ember` und `--sage`, damit der Stern
 *    in allen acht Paletten zur Seite passt.
 */
import { bahnFortschritt, sternHaltepunkte, sternstand } from "./tagesszene.js";
import type { Sonnenzeiten } from "./tagesszene.js";
import { gesteuert } from "./ruckel-schalter.js";

/** Derselbe Schlüssel, unter dem die Zuhause-Ansicht ihren Stand merkt. */
const GERUEST_KEY = "vespera.zuhause.stand";

/**
 * Die zuletzt bekannten Sonnenzeiten aus dem gemerkten Zuhause-Stand.
 *
 * ⚠️ Sie können fehlen — dann rechnet {@link bahnFortschritt} mit groben
 *    Stunden weiter. Für ein Zeichen, das eine Stimmung trägt, ist das genug;
 *    ein leerer Kopf wäre schlechter als ein ungefährer.
 */
export function gemerkteSonne(): Partial<Sonnenzeiten> | null {
  try {
    const raw = sessionStorage.getItem(GERUEST_KEY);
    if (!raw) return null;
    const s = (JSON.parse(raw) as { sonne?: unknown }).sonne;
    if (!s || typeof s !== "object") return null;
    const o = s as { aufgang?: unknown; untergang?: unknown };
    return {
      aufgang: typeof o.aufgang === "string" ? o.aufgang : undefined,
      untergang: typeof o.untergang === "string" ? o.untergang : undefined,
    };
  } catch {
    return null;
  }
}

/** Steht die Sonne gerade unter dem Horizont? */
function istNacht(jetzt: Date, sonne: Partial<Sonnenzeiten> | null): boolean {
  const min = (iso?: string): number | null => {
    const m = iso ? /T(\d{2}):(\d{2})/.exec(iso) : null;
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  };
  const auf = min(sonne?.aufgang) ?? 6 * 60 + 30;
  const unter = min(sonne?.untergang) ?? 20 * 60;
  const nun = jetzt.getHours() * 60 + jetzt.getMinutes();
  return nun < auf || nun >= unter;
}

/**
 * Den Stern auf die Stunde bringen.
 *
 * Geschrieben wird an das Wurzelelement, nicht an den Stern selbst: die
 * Kopfleiste wird an mehreren Stellen neu gezeichnet, ein Wert an `:root`
 * überlebt das.
 */
function zeichneStern(jetzt: Date = new Date()): void {
  const sonne = gemerkteSonne();
  const nacht = istNacht(jetzt, sonne);
  const stand = sternstand(nacht, bahnFortschritt(jetzt, sonne ?? undefined, nacht));
  const { oben, unten } = sternHaltepunkte(stand.glut);
  const w = document.documentElement.style;
  w.setProperty("--stern-oben", `color-mix(in srgb, var(--ember) ${oben}%, var(--sage))`);
  w.setProperty("--stern-unten", `color-mix(in srgb, var(--ember) ${unten}%, var(--sage))`);
  w.setProperty("--stern-hof", String(stand.hof));
  // Der Winkel ist eine SVG-Eigenschaft, keine CSS-Eigenschaft — er muss an
  // den Verlauf selbst.
  document
    .getElementById("vespera-stern")
    ?.setAttribute("gradientTransform", `rotate(${stand.winkel} 0.5 0.5)`);
}

let lauf: number | null = null;

/**
 * Den Stern mitlaufen lassen.
 *
 * Alle fünf Minuten, und sobald die App wieder sichtbar wird — wer das Handy
 * abends aus der Tasche holt, soll nicht den Mittag sehen.
 */
export function starteSternlauf(): void {
  if (lauf !== null) return;
  zeichneStern();
  lauf = window.setInterval(gesteuert("live", () => zeichneStern(), true), 5 * 60_000);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") zeichneStern();
  });
}
